import type { HTMLAttributes } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { getColorWithAlpha, Show } from '@elfsight/lib-utils';
import {
  Button,
  loadMoreButtonDarkThemeDefaults,
  loadMoreButtonLightThemeDefaults,
  Typography
} from '@elfsight/widget-ui';
import { scrollbarColoredMixin } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import tinycolor from 'tinycolor2';
import { isDisplayable } from '../components/displayable';
import { LocationPreview } from '../location-preview/location-preview';
import { useLocationsFilter } from '../locations-filter-provider';
import { useMapAPI } from '../map-api-context';
import { useUserLocation } from '../user-location-context';

export type DirectoryLocationsListProps = HTMLAttributes<HTMLDivElement> & {
  bottomSpacerSize?: number;
  previewWithActions?: boolean;
  detailedCard?: boolean;
};

export function DirectoryLocationsList({
  bottomSpacerSize = 0,
  previewWithActions = false,
  detailedCard = true,
  ...forwardingProps
}: DirectoryLocationsListProps) {
  const {
    textColor,
    directoryShowCount,
    directoryPerPage,
    previewCardElements,
    backgroundColor,
    previewCardStyle
  } = useSettings();
  const { t } = useLingui();
  const { isActiveFilters } = useLocationsFilter();
  const { locations } = useUserLocation();
  const { mapAPIRef } = useMapAPI();

  const [currentHovered, setCurrentHovered] = useState<string | undefined>(
    undefined
  );
  const [page, setPage] = useState(1);

  const listRef = useRef<HTMLDivElement>(null);
  const locationRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const isPreviewDisplayable = isDisplayable(previewCardElements);
  const validLocations = locations.filter((location) => {
    return (
      location?.place &&
      location._fulfilled &&
      [
        isPreviewDisplayable('name', location.name),
        isPreviewDisplayable('status'),
        isPreviewDisplayable('address', location.address),
        isPreviewDisplayable('phone', location.phone),
        isPreviewDisplayable('website', location.website),
        isPreviewDisplayable('email', location.email)
      ].some(Boolean)
    );
  });
  const finalLocations = validLocations.slice(0, directoryPerPage * page);
  const hasNextLocations = validLocations.length > directoryPerPage * page;

  useEffect(() => {
    setPage(1);
  }, [directoryPerPage]);

  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
  };

  const onMarkerClick = (locationId?: string) => {
    const targetLocationIndex = validLocations.findIndex(
      ({ id }) => id === locationId
    );
    const targetLocationPage = targetLocationIndex / directoryPerPage + 1;
    if (targetLocationPage >= page) {
      setPage(targetLocationPage);
      const listNode = listRef.current;
      const locationNode = locationId && locationRefs.current[locationId];
      if (listNode && locationNode) {
        listNode.scrollTo({
          top: locationNode.offsetTop - locationNode.offsetHeight,
          behavior: 'smooth'
        });
      }
    }
  };

  useEffect(() => {
    const unsubscribeHoverCallback = mapAPIRef?.current?.onMarkerHover(
      setCurrentHovered,
      'hover-effect'
    );
    const unsubscribeClickCallback = !detailedCard
      ? mapAPIRef?.current?.onMarkerClick(
          onMarkerClick,
          'directory-location-list'
        )
      : undefined;

    return () => {
      unsubscribeHoverCallback?.();
      unsubscribeClickCallback?.();
    };
  }, [detailedCard]);

  const showCount = validLocations.length
    ? directoryShowCount
    : isActiveFilters;

  const count =
    isActiveFilters && !validLocations.length
      ? t`No locations found. Try adjusting your filters.`
      : t`${validLocations.length} locations`;

  return (
    <Container
      ref={listRef}
      {...forwardingProps}
      _bottomSpacerSize={bottomSpacerSize + (hasNextLocations ? 24 : 0)}
      _backgroundColor={backgroundColor}
      _textColor={textColor}
    >
      <Show when={showCount}>
        <Count
          textStyle={{ fontSize: 14 }}
          color={getColorWithAlpha(textColor, 0.5)}
        >
          {count}
        </Count>
      </Show>
      <List>
        {finalLocations.map((location) => (
          <StyledLocationPreview
            ref={(el) => (locationRefs.current[location.id] = el)}
            hoverState={currentHovered === location.id}
            tabIndex={0}
            key={location.id}
            _withDivider={previewCardStyle !== 'image-top'}
            _color={textColor}
            location={location}
            withActions={previewWithActions}
          />
        ))}
      </List>
      {hasNextLocations && (
        <StyledButton
          onClick={handleLoadMore}
          {...(tinycolor(backgroundColor).getBrightness() < 128
            ? loadMoreButtonDarkThemeDefaults
            : loadMoreButtonLightThemeDefaults)}
          text={t`Load More`}
        />
      )}
    </Container>
  );
}

const Container = styled.div<{
  _bottomSpacerSize: number;
  _backgroundColor: string;
  _textColor: string;
}>`
  overflow: auto;
  ${({ _textColor }) =>
    scrollbarColoredMixin(getColorWithAlpha(_textColor, 0.3))};
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  padding-bottom: ${({ _bottomSpacerSize }) => _bottomSpacerSize}px;
  background: ${({ _backgroundColor }) => _backgroundColor};
  pointer-events: all;
`;

const Count = styled(Typography)`
  padding: 12px 12px 0 12px;
  margin-bottom: 4px;
`;

const List = styled.nav``;

const StyledLocationPreview = styled(LocationPreview)<{
  _color: string;
  _withDivider: boolean;
}>`
  ${({ _withDivider, _color }) =>
    _withDivider &&
    css`
      &:not(:last-child) {
        border-bottom: 1px solid ${getColorWithAlpha(_color, 0.1)};
      }
    `}
`;

const StyledButton = styled(Button)`
  margin: 12px auto 0 auto;
`;
